import React, { useState } from "react";
import * as R from "ramda";
import { FormattedMessage } from "react-intl";
import { Button, Box, Grid, Icon, Text, Tooltip, HStack } from "@chakra-ui/react";

import { useOverlaySpinner } from "components/OverlaySpinner";
import { PaypalLogo } from "components/Logos";
import {
  exists,
  isNilOrEmpty,
  inIframe,
  useSessionJWT,
  useSessionCtx,
  usePostMessage,
  FLOWS,
  redirectWindowLocation,
  useAnalytics,
} from "utils";
import { blockedCTAProps, useTOSGate } from "components/JustInTimeTOS";
import { ErrorMessageDiv } from "components/Form";
import { logError } from "api/accounts-customer";
import usePayPalRedirect from "components/Paypal/usePayPalRedirect";
import { BsPaypal } from "react-icons/bs";
import { IoIosAddCircleOutline } from "react-icons/io";

// Named because processError below branches on them, not just logs them: one of the two is not a
// PayPal failure at all.
const FLOW_START_CREATE = "PAYPAL.START_CREATE";
const FLOW_SIGN_TOS = "PAYPAL.SIGN_TOS";

const tokenRegex = /token=(.+)/;
export const parseToken = (response) => {
  if (isNilOrEmpty(response)) return null;
  const uri = response.payload?.redirect_uri;
  if (isNilOrEmpty(uri)) return null;

  return R.pipe(
    R.prop("search"),
    R.match(tokenRegex),
    R.prop(1),
    // split off extra query string
    R.split("&"),
    R.prop(0),
  )(new URL(uri));
};

export const AddPaypalButton = ({
  amountExceedsMax,
  newUI,
  useTextUI,
  description,
  onPreClickCallback,
  prepareTokenOnLoad = true,
  requireTOS = false,
}) => {
  const analytics = useAnalytics();
  const { blockedByTOS, signTOS, signing, ensureNotBlocked } = useTOSGate({
    enabled: requireTOS,
  });
  const { sessionJWT } = useSessionJWT();
  const { sessionCtx, debug, setFlowState } = useSessionCtx();
  const { flowState } = sessionCtx;
  const { postMessage } = usePostMessage(FLOWS.PAYPAL, sessionCtx?.intCtx);
  const [redirect, setRedirect] = useState();
  const { showOverlaySpinner, hideOverlaySpinner } = useOverlaySpinner();
  const [isLoading, setIsLoading] = useState(false);
  const { redirectToken, getPayPalRedirectResponse } = usePayPalRedirect(prepareTokenOnLoad);

  if (redirect) {
    debug("paypalbutton__redirect", redirect);
    redirectWindowLocation(redirect);
  }

  const processStartCreateResponse = (response) => {
    debug("paypalbutton__processresponse", response.payload);
    setFlowState("paypal.redirect");

    if (inIframe()) {
      postMessage(`tilia.${FLOWS.PAYPAL}.redirect`, { redirect: response.payload.redirect_uri });
    } else {
      setRedirect(response.payload.redirect_uri);
    }
  };

  // flowLoc is a parameter rather than hardcoded: reusing this for a sign failure would otherwise
  // log it as a PayPal start-create failure.
  const processError = async (error, flowLoc = FLOW_START_CREATE) => {
    await logError(sessionJWT, { flowLoc, error });
    if (flowLoc === FLOW_SIGN_TOS) {
      // A ToS sign failure is not a PayPal failure - startCreatePaypal was never called - so it
      // does not travel to the integrator on the PayPal channel. That is the same mis-attribution
      // CheckoutContainer's split try blocks exist to avoid, and the flowLoc above only fixes the
      // log. The user-visible message comes from signError in the consent block.
      return;
    }
    if (inIframe()) {
      postMessage(`tilia.${FLOWS.PAYPAL}.error`, { error });
    } else {
      // redirect error?
      debug("paypalbutton__fullpage__error?", error);
    }
  };

  const handleClick = async () => {
    if (isLoading || signing) return;

    let flowLoc = FLOW_START_CREATE;
    setIsLoading(true);
    // Up before the gate as well as the sign: ensureNotBlocked may have to wait out the status
    // fetch, and the useTextUI (HStack) and newUI (Box) variants have raw onClick and no disabled
    // concept to show for it.
    showOverlaySpinner();
    try {
      // A gate call rather than a `blockedByTOS` guard: a blocked click either scrolls to the
      // consent block or waits out an unresolved status and re-decides, and only the gate knows
      // which (see useTOSGate). Ahead of the analytics event so a blocked click is not a click.
      if (!(await ensureNotBlocked())) return;

      analytics.track("PayPal_Add_New_Button_Clicked");

      // Before minting the PayPal token, so a sign failure prepares no redirect
      flowLoc = FLOW_SIGN_TOS;
      await signTOS();

      flowLoc = FLOW_START_CREATE;
      const startCreateResponse = await getPayPalRedirectResponse();
      processStartCreateResponse(startCreateResponse);
    } catch (error) {
      processError(error, flowLoc);
    } finally {
      setIsLoading(false);
      hideOverlaySpinner();
    }
  };

  // TODO: Looks like we currently have 3 different views of this component.  Should limit that down
  let content = (
    <Tooltip label={amountExceedsMax ? "Amount exceeds maximum allowed" : ""} shouldWrapChildren>
      <Button
        data-testid="pm-add-paypal-button"
        variant="outline"
        colorScheme="gray"
        onClick={handleClick}
        // Presentation only on this variant - the handler guard above is what enforces the gate
        isDisabled={isLoading || amountExceedsMax || !exists(redirectToken) || blockedByTOS}
        width="full"
      >
        <Box mr={2} as="span">
          <FormattedMessage defaultMessage="Add" description="button label" />
        </Box>
        <PaypalLogo
          css={`
            height: 20px;
            width: auto;
          `}
        />
      </Button>
    </Tooltip>
  );

  if (useTextUI) {
    content = (
      <HStack
        data-testid="pm-add-paypal-text-ui"
        spacing="1"
        color="integrator.400"
        fontWeight="600"
        cursor="pointer"
        {...blockedCTAProps(blockedByTOS)}
        onClick={() => {
          if (onPreClickCallback) onPreClickCallback();
          handleClick();
        }}
      >
        <Icon as={IoIosAddCircleOutline} w="5" h="5" />
        <Text>
          <FormattedMessage defaultMessage="Add PayPal" description="button label" />
        </Text>
      </HStack>
    );
  } else if (newUI) {
    content = (
      <Box
        data-testid="pm-add-paypal-button"
        p="4"
        boxShadow="base"
        cursor="pointer"
        {...blockedCTAProps(blockedByTOS)}
        onClick={handleClick}
      >
        <Grid templateColumns="20px 1fr" gridGap="2" alignItems="center">
          <Icon as={BsPaypal} boxSize="5" color="integrator.500" />
          <Text fontWeight="bold">
            <FormattedMessage defaultMessage="Add" description="button label" /> PayPal
          </Text>
          <Box />
          <Box>{description}</Box>
        </Grid>
      </Box>
    );
  }

  return (
    <>
      {content}
      {redirect && <span data-testid="add-paypal-redirect"></span>}
      {flowState === "paypal.error" && (
        <ErrorMessageDiv
          css={`
            margin: 8px 0;
          `}
        >
          <FormattedMessage
            defaultMessage="There was an error with your request. Please try again."
            description="Error message"
          />
        </ErrorMessageDiv>
      )}
    </>
  );
};
